/**
 * 我的套票區塊
 * 列出會員的套票（課程／計次型），有剩餘次數者顯示 QR Code（內容為套票 ID），供核銷端掃描
 */

import { useState } from 'react';
import useSWR from 'swr';
import { QRCodeSVG } from 'qrcode.react';
import { SectionCard } from './SectionCard';
import { Loading } from './Loading';
import {
  getCustomerPackages,
  type CustomerPackageItem,
} from '../api/memberClient';
import { formatInTimeZone } from 'date-fns-tz';
import { Package, ChevronDown, ChevronUp } from 'lucide-react';

const TAIWAN_TZ = 'Asia/Taipei';

function PackageCard({ pkg }: { pkg: CustomerPackageItem }) {
  const [showQr, setShowQr] = useState(false);
  const isUnlimited = pkg.remaining_quantity === -1;
  const canRedeem =
    pkg.status === 'active' && (isUnlimited || pkg.remaining_quantity > 0);
  const expiryStr =
    pkg.expiry_date &&
    formatInTimeZone(new Date(pkg.expiry_date), TAIWAN_TZ, 'yyyy/MM/dd');

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Package className="shrink-0 w-5 h-5 text-gray-500" />
            <span className="font-medium text-gray-900 truncate">
              {pkg.product_name}
            </span>
            <span
              className={`shrink-0 text-xs px-2 py-0.5 rounded ${
                pkg.status === 'active'
                  ? 'bg-green-100 text-green-800'
                  : pkg.status === 'finished'
                    ? 'bg-gray-100 text-gray-600'
                    : 'bg-red-100 text-red-800'
              }`}
            >
              {pkg.status === 'active'
                ? '有效'
                : pkg.status === 'finished'
                  ? '已用完'
                  : '已過期'}
            </span>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            {isUnlimited
              ? '無限次（權益型）'
              : `剩餘 ${pkg.remaining_quantity} / ${pkg.initial_quantity} 次`}
          </p>
          {expiryStr && (
            <p className="text-sm text-gray-500">過期日：{expiryStr}</p>
          )}
        </div>
        {canRedeem && !isUnlimited && (
          <button
            type="button"
            onClick={() => setShowQr(!showQr)}
            className="shrink-0 flex items-center gap-1 text-sm text-teal-600 hover:text-teal-800"
          >
            {showQr ? (
              <>
                收起 QR <ChevronUp className="w-4 h-4" />
              </>
            ) : (
              <>
                出示 QR <ChevronDown className="w-4 h-4" />
              </>
            )}
          </button>
        )}
      </div>
      {canRedeem && !isUnlimited && showQr && (
        <div className="mt-4 flex flex-col items-center">
          <p className="text-xs text-gray-500 mb-2">
            請出示此 QR 供店家掃描核銷（扣一次）
          </p>
          <div className="bg-white p-3 rounded-lg border border-gray-200 inline-block">
            <QRCodeSVG value={pkg.id} size={180} level="M" />
          </div>
        </div>
      )}
      {canRedeem && isUnlimited && (
        <p className="text-xs text-gray-500 mt-2">
          權益型套票請出示會員身份核銷。
        </p>
      )}
    </div>
  );
}

interface PackagesBlockProps {
  customerId: string;
}

export function PackagesBlock({ customerId }: PackagesBlockProps) {
  const { data, isLoading, error } = useSWR(
    customerId ? ['customer-packages', customerId] : null,
    () => getCustomerPackages(customerId, { includeExpired: true }),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 60000,
    }
  );

  const packages = data?.data ?? [];
  const activeWithQty = packages.filter(
    p => p.status === 'active' && p.remaining_quantity > 0
  ).length;

  return (
    <SectionCard title="我的套票">
      {isLoading && (
        <div className="py-6">
          <Loading />
        </div>
      )}
      {!isLoading && error && (
        <p className="text-sm text-red-600">
          {error instanceof Error
            ? error.message
            : '無法載入套票，請稍後再試。'}
        </p>
      )}
      {!isLoading && !error && packages.length === 0 && (
        <p className="text-sm text-gray-500">目前沒有套票。</p>
      )}
      {!isLoading && !error && packages.length > 0 && (
        <div className="space-y-3">
          {activeWithQty > 0 && (
            <p className="text-sm text-gray-600">
              共 {packages.length} 筆，其中 {activeWithQty} 筆可出示 QR 核銷
            </p>
          )}
          {packages.map(p => (
            <PackageCard key={p.id} pkg={p} />
          ))}
        </div>
      )}
    </SectionCard>
  );
}
